import styled from 'styled-components';

/* Design bands are max-width, not the theme's min-width set. */
const abp = (key: string) => (props: any) => props.theme.awarenessBreakpoints && props.theme.awarenessBreakpoints[key];

const MAX_WIDTH = '1200px';
const DARK = '#161719';
const LIGHT_TEXT = '#FFFFFF';
const LIGHT_BODY = '#E6E6EE';
const BODY = '#161719';
const QUOTE_RULE = '#B9BAC5';
const MUTED = '#5F6068';
const FOCUS_BLUE = '#3A70D9';

/* ---------------------------------------------------------------- Hero ---- */

/* Not the landing page's full width banner. Here the image is a panel beside
   the copy on wide screens, and stacks above it when there is no room. */

const HeroSection = styled.section`
    background: ${DARK};
    display: grid;
    grid-template-columns: 0.82fr 1.18fr;
    align-items: stretch;

    @media screen and (max-width: ${abp('w860')}) {
        grid-template-columns: 1fr;
    }
`;

const HeroMedia = styled.div`
    position: relative;
    min-height: 500px;
    overflow: hidden;

    @media screen and (max-width: ${abp('w860')}) {
        min-height: 227px;
    }

    picture {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center 32%;
    }
`;

const HeroCopy = styled.div`
    padding: 64px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media screen and (max-width: ${abp('w900')}) {
        padding: 48px 32px;
    }
`;

const Eyebrow = styled.p`
    margin: 0;
    font-family: Overpass;
    font-weight: 700;
    font-size: 15px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${LIGHT_TEXT};
`;

const HeroTitle = styled.h1`
    margin: 20px 0 0;
    font-family: Anton;
    font-weight: 400;
    font-size: 60px;
    line-height: 63px;
    text-transform: uppercase;
    color: ${LIGHT_TEXT};

    @media screen and (max-width: ${abp('w640')}) {
        font-size: 48px;
        line-height: 50px;
    }
`;

const HeroText = styled.div`
    p {
        margin: 24px 0 0;
        max-width: 560px;
        font-family: Overpass;
        font-weight: 300;
        font-size: 20px;
        line-height: 34px;
        color: ${LIGHT_BODY};
    }
`;

/* ------------------------------------------------------- Feature blocks ---- */

/* The heading and its intro sit in the first cell of the same grid as the
   items, which is why the lead is a grid child and not a column of its own. */

const FeatureSection = styled.section`
    background: ${DARK};
`;

const FeatureInner = styled.div`
    max-width: ${MAX_WIDTH};
    margin: 0 auto;
    padding: 48px 32px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 48px;

    @media screen and (max-width: ${abp('w900')}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${abp('w560')}) {
        grid-template-columns: 1fr;
    }
`;

const FeatureLead = styled.div``;

const FeatureHeading = styled.h2`
    margin: 0;
    font-family: Anton;
    font-weight: 400;
    font-size: 32px;
    line-height: 1.05;
    text-transform: uppercase;
    color: ${LIGHT_TEXT};
`;

const FeatureIntro = styled.div`
    p {
        margin: 24px 0 0;
        font-family: Overpass;
        font-weight: 300;
        font-size: 20px;
        line-height: 28px;
        color: ${LIGHT_BODY};
    }
`;

const FeatureItem = styled.div``;

const FeatureItemHeading = styled.h3`
    margin: 0;
    font-family: Overpass;
    font-weight: 600;
    font-size: 24px;
    line-height: 1.18;
    text-transform: uppercase;
    color: ${LIGHT_TEXT};
`;

const FeatureItemText = styled.div`
    p {
        margin: 24px 0 0;
        font-family: Overpass;
        font-weight: 300;
        font-size: 20px;
        line-height: 28px;
        color: ${LIGHT_BODY};
    }
`;

/* -------------------------------------------------------- Testimonials ---- */

const TestimonialsSection = styled.section`
    background: #fff;
`;

const SectionInner = styled.div`
    max-width: ${MAX_WIDTH};
    margin: 0 auto;
    padding: 48px 32px;
`;

const TestimonialsHeading = styled.h2`
    margin: 0 0 40px;
    font-family: Anton;
    font-weight: 400;
    font-size: 36px;
    line-height: 38px;
    text-transform: uppercase;
    color: ${BODY};
`;

const TestimonialsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 48px;

    @media screen and (max-width: ${abp('w900')}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${abp('w560')}) {
        grid-template-columns: 1fr;
    }
`;

/* Column layout so the quote takes the slack and the name lands on the bottom
   edge of every card, however long the quotes are. */
const TestimonialCard = styled.div`
    display: flex;
    flex-direction: column;
`;

const TestimonialMedia = styled.div`
    margin: 0 0 32px;

    picture, img {
        display: block;
        width: 100%;
    }

    img {
        aspect-ratio: 1 / 1;
        object-fit: cover;
    }
`;

/* The serif face and the hairline on the left are what mark this out as a
   quote; the rest of the page is the sans body copy. */
const TestimonialQuote = styled.div`
    flex: 1;
    border-left: 1px solid ${QUOTE_RULE};
    padding-left: 20px;

    p {
        margin: 0;
        font-family: Lora;
        font-weight: 400;
        font-size: 20px;
        line-height: 28px;
        color: ${BODY};
    }
`;

const TestimonialName = styled.p`
    margin: 32px 0 0;
    padding-left: 20px;
    font-family: Overpass;
    font-weight: 700;
    font-size: 16px;
    color: ${BODY};
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
`;

/* ---------------------------------------------------------------- Ways ---- */

/* The same grid the landing page renders, minus the reveal: a reader on this
   page has already signed up, so it is always shown. */

const WaysSection = styled.section`
    background: #fff;
`;

const WaysInner = styled.div`
    max-width: ${MAX_WIDTH};
    margin: 0 auto;
    padding: 32px 32px 64px;
`;

const WaysEyebrow = styled.p`
    margin: 0 0 12px;
    font-family: Overpass;
    font-weight: 700;
    font-size: 16px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: ${MUTED};
`;

const WaysHeading = styled.h2`
    margin: 0 0 40px;
    font-family: Anton;
    font-weight: 400;
    font-size: 36px;
    line-height: 38px;
    text-transform: uppercase;
    color: ${BODY};
`;

const WaysGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 40px;
    row-gap: 44px;

    @media screen and (max-width: ${abp('w900')}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${abp('w560')}) {
        grid-template-columns: 1fr;
    }
`;

const WayCardImage = styled.div`
    position: relative;
    display: block;
    width: 100%;
    aspect-ratio: 368 / 173;
    overflow: hidden;

    picture, img {
        display: block;
        width: 100%;
        height: 100%;
    }

    img {
        object-fit: cover;
        object-position: center;
        filter: grayscale(1);
    }
`;

const WayCardTitle = styled.h3`
    margin: 0;
    font-family: Overpass;
    font-weight: 600;
    font-size: 24px;
    line-height: 28px;
    text-transform: uppercase;
    color: ${BODY};
`;

/* Block rather than an inline-flex row so the chevron follows the last word
   when the label wraps, instead of centring beside the whole block. */
const WayCardCta = styled.span`
    display: block;
    font-family: Overpass;
    font-weight: 300;
    font-size: 20px;
    line-height: 28px;
    color: ${BODY};

    > svg {
        display: inline-block;
        vertical-align: middle;
        margin-left: 2px;
        width: 16px;
        height: 16px;
    }
`;

// The whole card is the link, as in the design, so there is no anchor inside it.
const WayCard = styled.a`
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-decoration: none;

    &:focus-visible {
        outline: 3px solid ${FOCUS_BLUE};
        outline-offset: 3px;
    }
`;

export {
    HeroSection,
    HeroMedia,
    HeroCopy,
    Eyebrow,
    HeroTitle,
    HeroText,
    FeatureSection,
    FeatureInner,
    FeatureLead,
    FeatureHeading,
    FeatureIntro,
    FeatureItem,
    FeatureItemHeading,
    FeatureItemText,
    TestimonialsSection,
    SectionInner,
    TestimonialsHeading,
    TestimonialsGrid,
    TestimonialCard,
    TestimonialMedia,
    TestimonialQuote,
    TestimonialName,
    WaysSection,
    WaysInner,
    WaysEyebrow,
    WaysHeading,
    WaysGrid,
    WayCard,
    WayCardImage,
    WayCardTitle,
    WayCardCta,
};
